import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useUserData } from "../../contexts/UserContext";
import { useLang } from "../../contexts/LangContext";
import { useLogSession } from "../../contexts/LogSessionContext";
import { storageService, shouldShowExtraInfo } from "../../services/storage.service";
import { fishDatabase, FishSpecies } from "../../data/fish_db";
import FishWholeIcon from "../../assets/images/icons/fish_whole.png";
import FishPieceIcon from "../../assets/images/icons/fish_piece.png";
import FishPortionIcon from "../../assets/images/icons/fish_portion.png";
import PortionSlider from "../../components/PortionSlider";
import { ModalDialog } from "../../utils/ModalDialog";
import { convertNumberInCurrentLang } from "../../utils/misc";
import {
  getAvailablePortionTypes,
  getAvailablePieceTypes,
  getMiddleWeightForPieceType,
  getImagePath,
  getFishSerialNumber,
  getWeightsForPortionType,
  hasImagesForRecipe
} from "../../utils/recipeImageUtils";

export default function WhichAmount() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const {user} = useUserData();
  const { currentDayRecord, currentMealRecord, newFishItem, setNewFishItem, commitFishItemToMeal } =
    useLogSession();
  const [fish, setFish] = useState<FishSpecies | null>(null);

  const checkLogSession = () => {
    if (!currentDayRecord) {
      navigate("../any-fish-today", { replace: true });
      return;
    } else if (!currentMealRecord) {
      navigate("../which-meal", { replace: true });
      return;
    } else if (!newFishItem) {
      navigate("../which-fish", { replace: true });
      return;
    }
  };

  useEffect(() => {
    checkLogSession();
  }, [currentDayRecord, currentMealRecord, newFishItem, navigate]);

  checkLogSession();

  const [availablePortionTypes, setAvailablePortionTypes] = useState<
    ("whole" | "piece" | "portion")[]
  >([]);
  const [availablePieceTypes, setAvailablePieceTypes] = useState<string[]>([]);

  const [portionType, setPortionType] = useState<
    "whole" | "piece" | "portion" | null
  >(null);
  const [pieceType, setPieceType] = useState<string | null>(null);
  const [numberOfPortions, setNumberOfPortions] = useState(1);
  const [currentPortionGrams, setCurrentPortionGrams] = useState(0);
  const [customAmount, setCustomAmount] = useState("");
  const [customWeightInputFocused, setCustomWeightInputFocused] = useState(false);
  const [portionSliderOpacity, setPortionSliderOpacity] = useState(1.0);
  const [recipeHasImages, setRecipeHasImages] = useState(false);
  
  // Track previous values to detect actual changes (not initial loads)
  const prevPortionTypeRef = useRef<string | null>(null);
  const prevPieceTypeRef = useRef<string | null>(null);
  const isInitializedRef = useRef(false);
  const skipNextPieceTypeEffectRef = useRef(true);

  useEffect(() => {
    if (!newFishItem) return;

    const _fish = fishDatabase.find((f) => f.id === newFishItem.fish_id);
    if (!_fish) {
      // "Other" fish has no portion types, piece types, or recipe images.
      // Reset any stale state left over from a previously selected fish.
      setFish(null);
      setRecipeHasImages(false);
      setAvailablePortionTypes([]);
      setAvailablePieceTypes([]);
      setPortionType(null);
      setPieceType(null);
      setNumberOfPortions(1);
      setCurrentPortionGrams(0);
      setCustomAmount("");
      isInitializedRef.current = true;
      return;
    }
    setFish(_fish);

    // Check if recipe has images
    const recipe = newFishItem.recipe || "curry";
    const hasImages = hasImagesForRecipe(_fish.id, recipe);
    setRecipeHasImages(hasImages);

    // Get available portion types from recipe images (only if images exist)
    const availableTypes = hasImages 
      ? getAvailablePortionTypes(_fish.id, recipe) as ("whole" | "piece" | "portion")[]
      : ["whole", "piece", "portion"] as ("whole" | "piece" | "portion")[];
    
    setAvailablePortionTypes(availableTypes);

    // Set default portion type
    if (
      newFishItem.portion_type &&
      availableTypes.includes(newFishItem.portion_type as "whole" | "piece" | "portion")
    ) {
      setPortionType(newFishItem.portion_type as "whole" | "piece" | "portion");
      
      // Set piece type if portion type is "piece" and piece type is available
      if (newFishItem.portion_type === "piece" && newFishItem.piece_type && hasImages) {
        const pieceTypes = getAvailablePieceTypes(_fish.id, recipe);
        if (pieceTypes.includes(newFishItem.piece_type)) {
          setPieceType(newFishItem.piece_type);
        }
      }
    } else if (availableTypes.length > 0) {
      setPortionType(availableTypes[0]);
    }

    // Set default number of portions
    setNumberOfPortions(newFishItem.number_of_portions || 1);

    // Set current portion grams based on fish item
    // If user is returning from ExtraInfo, preserve the slider position (even non-checkpoint values)
    if (newFishItem.weight_per_portion && newFishItem.weight_per_portion > 0) {
      setCurrentPortionGrams(newFishItem.weight_per_portion);
    } else {
      setCurrentPortionGrams(getMedianWeight() || 50);
    }
    
    // Mark initialization as complete
    isInitializedRef.current = true;
    // The next piece-type effect run will have stale closure values (portionType from prior render).
    // Skip it once to avoid clearing restored pieceType.
    skipNextPieceTypeEffectRef.current = true;
  }, [newFishItem]);

  useEffect(() => {
    if (!newFishItem || !isInitializedRef.current) return;
    if (skipNextPieceTypeEffectRef.current) {
      skipNextPieceTypeEffectRef.current = false;
      return;
    }
    if (!portionType) return;

    const _fish = fishDatabase.find((f) => f.id === newFishItem.fish_id);

    if (!_fish) return;
    setFish(_fish);
    
    // Load available piece types if portion type is "piece" and recipe has images
    if (portionType === "piece" && recipeHasImages) {
      const recipe = newFishItem.recipe || "curry";
      const pieceTypes = getAvailablePieceTypes(_fish.id, recipe);
      setAvailablePieceTypes(pieceTypes);
      
      // Only set default piece type if not already set (to avoid overriding restored values)
      if (!pieceType && pieceTypes.length > 0) {
        setPieceType(pieceTypes[0]);
      }
    } else {
      setAvailablePieceTypes([]);
      if (portionType !== "piece") {
        setPieceType(null);
      }
    }    
  }, [portionType, recipeHasImages]);
 
  const getMedianWeight = () => {
    if (!newFishItem || !fish || !portionType) return 0;
    const recipe = newFishItem.recipe || "curry";
    const weights = getWeightsForPortionType(fish.id, recipe, portionType, pieceType || undefined);
    if (weights.length === 0) return 0;
    const sorted = [...weights].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);  
    return sorted.length % 2 !== 0
      ? sorted[mid]
      : (sorted[mid - 1] + sorted[mid]) / 2;
  };

  const getClosestCheckpointWeight = () => {
    if (!newFishItem || !fish || !portionType) return 0;
    const recipe = newFishItem.recipe || "curry";
    const weights = getWeightsForPortionType(fish.id, recipe, portionType, pieceType || undefined);
    if (!weights || weights.length === 0) return 0;

    // Find closest weight
    const closestWeight = weights.reduce((prev, curr) => {
      return Math.abs(curr - currentPortionGrams) < Math.abs(prev - currentPortionGrams) ? curr : prev;
    });
    return closestWeight;
  };

  // Snap to closest checkpoint weight when portion type or piece type actually changes
  // But NOT on initial load when restoring previous values
  useEffect(() => {
    // Check if this is an actual change (not initial setting)
    const portionTypeChanged = prevPortionTypeRef.current !== null && prevPortionTypeRef.current !== portionType;
    const pieceTypeChanged = prevPieceTypeRef.current !== null && prevPieceTypeRef.current !== pieceType;
    
    // Update refs for next comparison
    prevPortionTypeRef.current = portionType;
    prevPieceTypeRef.current = pieceType;
    
    // Only snap if there was an actual change
    if (portionTypeChanged || pieceTypeChanged) {
      const closestWeight = getClosestCheckpointWeight();    
      setCurrentPortionGrams(closestWeight);
      // Clear custom amount when snapping
      setCustomAmount("");
    }
  }, [portionType, pieceType]);

  // When custom amount is being entered, fade out the portion slider
  useEffect(() => {
    if (customWeightInputFocused || customAmount) {
      setPortionSliderOpacity(0.5);
    } else {
      setPortionSliderOpacity(1.0);
    }
  }, [customWeightInputFocused, customAmount]);


  // On customWeightInputFocused, after a delay scroll the inout into view on mobile as the keyboard may cover it
  useEffect(() => {
    if (customWeightInputFocused) { 
      setTimeout(() => {
        const inputElement = document.querySelector('input[type="number"]');  
        if (inputElement) {
          inputElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);
    }
  }, [customWeightInputFocused]);


  const handleSubmit = async () => {
    if (!newFishItem) return;

    // For regular fish, require a valid fish record, portion type and portions
    if (!isOtherFish) {
      if (!fish) return;

      // Validate portion type and number of portions
      if (!portionType) {
        await ModalDialog.warning(
          t("whichAmount_NoAmountAlertMessage"),
          t("whichAmount_NoAmountAlertTitle")
        );
        return;
      }
      if (numberOfPortions < 1) {
        await ModalDialog.warning(
          t("whichAmount_SelectValidPortionCount"),
          t("whichAmount_SelectValidPortionCount")
        );
        return;
      }
    }

    let estimated_weight = 0;
    const unit = user?.appData?.weight_unit || "gm"; // Use user's weight unit preference

    if (customAmount) {
      // User entered custom amount - use that
      estimated_weight = parseFloat(customAmount);
    } else if (isOtherFish) {
      // "Other" fish has no portion types or images - direct weight is required
      await ModalDialog.warning(
        t("whichAmount_NoAmountAlertMessage"),
        t("whichAmount_NoAmountAlertTitle")
      );
      return;
    } else if (recipeHasImages) {
      // Recipe has images and slider is visible - use slider value
      const portion_weight = currentPortionGrams;
      estimated_weight = portion_weight * numberOfPortions;
    } else {
      // No recipe images (slider hidden) and no custom amount entered
      // Show warning that amount is required
      await ModalDialog.warning(
        t("whichAmount_NoAmountAlertMessage"),
        t("whichAmount_NoAmountAlertTitle")
      );
      return;
    }

    if (!estimated_weight || estimated_weight <= 0) {
      await ModalDialog.warning(
        t("whichAmount_ChooseValidAmount"),
        t("whichAmount_ChooseValidAmount")
      );
      return;
    }    

    setNewFishItem({
      ...newFishItem,
      portion_type: isOtherFish ? "portion" : portionType as "whole" | "piece" | "portion",
      piece_type: !isOtherFish && portionType === "piece" ? pieceType || undefined : undefined,
      number_of_portions: isOtherFish ? 1 : numberOfPortions || 1,
      weight_per_portion: isOtherFish ? estimated_weight : currentPortionGrams || 0,
      estimated_weight: estimated_weight || 0,
      weight_unit: unit || "gm",
    });

    // Skip the Extra Info page when it would render nothing (no soft-bone
    // toggle and every extra-info field disabled) and go straight to review.
    const settings = await storageService.getSettings();
    const hasSoftBone = fish?.has_soft_bone || false;
    navigate(
      shouldShowExtraInfo(settings, hasSoftBone)
        ? "../extra-info"
        : "../review-fish-item"
    );
  };

  const isOtherFish = newFishItem?.fish_id === "other";

  return (
    <div className="w-full grow flex flex-col items-center justify-start gap-6 px-scr pt-8 text-center">
      <div className={`w-full grow flex flex-col gap-4 ${isOtherFish ? "justify-start" : "justify-between"}`}>
        <div className="flex flex-col items-center gap-4">
          <h1 className="text-2xl font-bold text-rose-700">
            {t("whichAmount_Heading").replace(
              "{fish}",
              lang === "bn"
                ? fish?.local_name || newFishItem?.fish_local_name || ""
                : fish?.english_name || newFishItem?.fish_english_name || ""
            )}
          </h1>

          {!isOtherFish && availablePortionTypes.length > 0 && (
            <>
              <h1 className="text-xl font-bold text-blue-900">
                {t("whichAmount_selectPortionType")}
              </h1>
              <div className="flex flex-wrap justify-center gap-2 text-xl mb-2">
            {availablePortionTypes.includes("whole") && (
              <div
                className={`flex flex-col items-center justify-center p-1 rounded-xl transition-colors duration-200 ${
                  portionType === "whole" ? "bg-pink-600" : "bg-gray-100"
                }`}
                onClick={() => setPortionType("whole")}
              >
                <img
                  src={FishWholeIcon}
                  alt="Whole Fish"
                  className="inline-block w-24 h-24 rounded-lg"
                />
                <h6
                  className={`text-center ${
                    portionType === "whole" ? "text-white" : "text-gray-800"
                  }`}
                >
                  {t(`portionType_whole` as any)}
                </h6>
              </div>
            )}
            {availablePortionTypes.includes("piece") && (
              <div
                className={`flex flex-col items-center justify-center p-1 rounded-xl transition-colors duration-200 ${
                  portionType === "piece" ? "bg-pink-600" : "bg-gray-100"
                }`}
                onClick={() => setPortionType("piece")}
              >
                <img
                  src={FishPieceIcon}
                  alt="Piece of Fish"
                  className="inline-block w-24 h-24 rounded-lg"
                />
                <h6
                  className={`text-center ${
                    portionType === "piece" ? "text-white" : "text-gray-800"
                  }`}
                >
                  {t(`portionType_piece` as any)}
                </h6>
              </div>
            )}
            {availablePortionTypes.includes("portion") && (
              <div
                className={`flex flex-col items-center justify-center p-1 rounded-xl transition-colors duration-200 ${
                  portionType === "portion" ? "bg-pink-600" : "bg-gray-100"
                }`}
                onClick={() => setPortionType("portion")}
              >
                <img
                  src={FishPortionIcon}
                  alt="Portion of Fish"
                  className="inline-block w-24 h-24 rounded-lg"
                />
                <h6
                  className={`text-center ${
                    portionType === "portion" ? "text-white" : "text-gray-800"
                  }`}
                >
                  {t(`portionType_portion` as any)}
                </h6>
              </div>
            )}
          </div>
            </>
          )}

          {/* Piece Type Selector - shown when portion type is "piece" */}
          {portionType === "piece" && availablePieceTypes.length > 0 && (
            <div className="w-full flex flex-col items-center gap-3 my-4">
              <h1 className="text-xl font-bold text-blue-900">
                {t("whichAmount_selectPieceType")}
              </h1>
              <div className="flex flex-wrap justify-center gap-3">
                {availablePieceTypes.map((pType) => {
                  const middleWeight = getMiddleWeightForPieceType(
                    fish?.id || "",
                    newFishItem?.recipe || "curry",
                    pType
                  );
                  const serialNumber = getFishSerialNumber(fish?.id || "");
                  const imagePath = middleWeight && serialNumber
                    ? getImagePath(
                        fish?.id || "",
                        serialNumber,
                        newFishItem?.recipe || "curry",
                        "piece",
                        middleWeight,
                        pType
                      )
                    : "";

                  return (
                    <div
                      key={pType}
                      className={`flex flex-col items-center justify-center p-1 rounded-xl transition-colors duration-200 cursor-pointer ${
                        pieceType === pType ? "bg-pink-600" : "bg-gray-100"
                      }`}
                      onClick={() => setPieceType(pType)}
                    >
                      <div className="w-20 h-20 overflow-hidden rounded-lg flex items-center justify-center bg-white">
                        {imagePath && (
                          <img
                            src={imagePath}
                            alt={`${pType} piece`}
                            className="min-w-full min-h-full object-cover"
                            style={{ transform: "scale(2)" }}
                          />
                        )}
                      </div>
                      <h6
                        className={`text-sm text-center mt-1 ${
                          pieceType === pType ? "text-white" : "text-gray-800"
                        }`}
                      >
                        {t(`pieceType_${pType}` as any)}
                      </h6>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Portion size buttons */}
          {portionType && (portionType !== "piece" || pieceType) && (
            <div className="w-full flex flex-col items-center">
              <div className="flex items-center justify-center gap-4 mb-4 text-pink-600">
                <button
                  onClick={() => {
                    if (numberOfPortions < 2) return;
                    setNumberOfPortions(numberOfPortions - 1);
                  }}
                  className="h-10 w-10 flex items-center justify-center bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="size-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 12h14"
                    />
                  </svg>
                </button>

                <span className="text-2xl text-pink-600 font-bold">
                  {convertNumberInCurrentLang(lang, numberOfPortions)}
                </span>

                <button
                  onClick={() => setNumberOfPortions(numberOfPortions + 1)}
                  className="h-10 w-10 flex items-center justify-center bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="size-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 5v14m7-7H5"
                    />
                  </svg>
                </button>
              </div>
              {recipeHasImages && (
                <>
                  <h1 className="text-xl font-bold text-blue-900 my-4">
                    {t("whichAmount_selectPortionSize")}
                  </h1>
                  <div className="w-full" style={{ opacity: portionSliderOpacity }}>
                    <PortionSlider
                      onChange={(val) => setCurrentPortionGrams(val)}
                      fishId={fish?.id || ""}
                      recipe={newFishItem?.recipe || "curry"}
                      portionType={portionType}
                      pieceType={pieceType || undefined}
                      n_portions={numberOfPortions}
                      initialWeight={currentPortionGrams}                
                    />
                  </div>
                  {numberOfPortions > 1 && (
                    <p className="text-base font-semibold text-gray-600">
                      {t("totalWeight")}:{" "}
                      <strong>
                        {convertNumberInCurrentLang(
                          lang,
                          currentPortionGrams * numberOfPortions
                        )}{" "}
                        {t("unit_gm")}
                      </strong>
                    </p>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <div className={`flex flex-col items-center w-full gap-4 ${isOtherFish ? "pt-20" : "py-4"}`}>
          {isOtherFish ? (
            <label
              htmlFor="other-fish-weight-input"
              className="text-xl font-bold text-blue-900"
            >
              {t("whichAmount_otherDirectInputLabel")}
            </label>
          ) : (
            <div className="text-sm text-gray-600 mt-2">
              {t("whichAmount_or")}
            </div>
          )}
          {/* When the input is focused, or has value, the portion slider will be faded out to 50% opacity */}

          <div className="relative w-full max-w-sm">
            <input
              id="other-fish-weight-input"
              type="number"
              className="input-base w-full"
              placeholder={t("whichAmount_customPlaceholder")}
              value={customAmount}
              onChange={(e) => setCustomAmount(e.target.value)}
              onFocus={() => setCustomWeightInputFocused(true)}
              onBlur={() => setCustomWeightInputFocused(false)}
            />

            {/*
            Add a close "X" button inside the input to clear the custom amount on the left side
            */}
            {customAmount && (
              <button
                className="absolute left-0 top-1/2 -translate-y-1/2 ml-3 text-gray-500 hover:text-gray-700 border border-gray-300 rounded-full p-1"   
                onClick={() => setCustomAmount("")}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-4"> 
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* <button
            onClick={handleSubmit}
            className="button-base button-bg-grad-green mt-2"
            disabled={!portionType && !customAmount}
          >
            {t("continue")}
          </button> */}
          <button
            className="w-full max-w-sm flex items-center justify-center gap-2 button-base button-bg-grad-2 mt-4 mb-8"
            onClick={handleSubmit}
          >
            <span>{t("whichAmount_AddItem")}</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="size-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
